import { Button } from '@/components/ui/button'
import { COMPETITIONS_LIST } from '@/lib/competitions'
import { ChevronLeft, CreditCard, Loader2 } from 'lucide-react'
import type { LeagueFormData } from './useRegisterLeague'

interface StepPaymentProps {
  formData: LeagueFormData
  loading: boolean
  error: string | null
  cancelled: boolean
  registrationFee: number | null
  onSubmit: () => void
  onBack: () => void
}

export function StepPayment({ formData, loading, error, cancelled, registrationFee, onSubmit, onBack }: StepPaymentProps) {
  const comp = COMPETITIONS_LIST.find(c => c.code === formData.competitionCode)

  return (
    <div>
      <h2 className="text-xl font-bold text-zinc-800 dark:text-zinc-100 mb-1">Confirm your league</h2>
      <p className="text-zinc-500 dark:text-zinc-400 text-sm mb-6">
        Review your details below, then you'll be taken to Stripe to complete payment.
      </p>

      {/* League summary */}
      <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-lg p-5 mb-6 space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-sm text-zinc-500 dark:text-zinc-400">League</span>
          <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200">{formData.name}</span>
        </div>
        {comp && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-zinc-500 dark:text-zinc-400">Competition</span>
            <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200 flex items-center gap-1.5">
              {comp.logo ? (
                <img src={comp.logo} alt="" className="w-4 h-4 object-contain inline-block" />
              ) : (
                <span>{comp.flag}</span>
              )}
              {comp.name}
            </span>
          </div>
        )}
        <div className="flex justify-between items-center">
          <span className="text-sm text-zinc-500 dark:text-zinc-400">Season</span>
          <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200">{formData.season}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-zinc-500 dark:text-zinc-400">Members</span>
          <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200">Up to 15 players</span>
        </div>
        <div className="border-t border-zinc-200 dark:border-zinc-700 pt-3 flex justify-between items-center">
          <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Season Pass</span>
          <span className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">
            {registrationFee != null ? `$${registrationFee.toFixed(2)}` : '—'}
          </span>
        </div>
      </div>

      <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-4">
        Have a promo code? You can apply it at checkout.
      </p>

      {cancelled && (
        <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4">
          <p className="text-sm text-amber-700 dark:text-amber-300">
            Payment was cancelled. You can try again when you're ready.
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} disabled={loading}>
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <Button onClick={onSubmit} className="flex-1" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Redirecting to checkout...
            </>
          ) : (
            <>
              <CreditCard className="mr-2 h-4 w-4" />
              Proceed to Payment
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
